import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Users, Shield, Check, Star } from 'lucide-react';
import { usePageSeo } from './seo';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// Shared lightweight top bar (kept at module scope so it isn't redefined on every render).
// Keeps this page usable both logged in and logged out.
function TopBar({ navigate }) {
  return (
    <header className="bg-card border-b border-border sticky top-0 z-40 shadow-custom">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-4 flex items-center justify-between">
        <button onClick={() => navigate('/')} className="flex items-center gap-3" data-testid="for-organisers-logo">
          <img src="/logo.png" alt="Gatherly" className="h-10 w-10" onError={(e) => (e.target.style.display = 'none')} />
          <h1 className="text-2xl font-bold text-primary">Gatherly</h1>
        </button>
        <nav className="flex items-center gap-4">
          <button onClick={() => navigate('/login')} className="text-sm hover:text-primary" data-testid="for-organisers-login">Login</button>
          <button onClick={() => navigate('/signup')} className="px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium" data-testid="for-organisers-signup">Sign Up</button>
        </nav>
      </div>
    </header>
  );
}

export default function ForOrganisersPage() {
  const navigate = useNavigate();
  usePageSeo({
    title: 'List Your Event Free | Gatherly',
    description: 'List your event free on Gatherly. Reach a global audience celebrating culture. No subscription required.',
  });
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    social_link: '',
    event_types: '',
    city: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const handleChange = (field) => (e) =>
    setFormData({ ...formData, [field]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg('');

    // Basic required-field validation (beyond HTML5 required)
    const required = ['full_name', 'email', 'social_link', 'event_types', 'city'];
    for (const f of required) {
      if (!String(formData[f] || '').trim()) {
        setErrorMsg('Please fill in all required fields.');
        return;
      }
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/launch-partner/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setSubmitted(true);
      } else {
        const data = await response.json().catch(() => ({}));
        setErrorMsg(data.detail || 'Something went wrong. Please try again.');
      }
    } catch (err) {
      setErrorMsg('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <TopBar navigate={navigate} />

      {/* Hero */}
      <section className="hero-gradient py-16 sm:py-20" data-testid="for-organisers-hero">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-10 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold mb-4" style={{ color: '#3B2314' }} data-testid="for-organisers-heading">
            List your event free. Reach the People.
          </h1>
          <p className="text-lg sm:text-xl" style={{ color: '#3B2314' }} data-testid="for-organisers-subtext">
            Gatherly is the home for Afro-Caribbean and cultural events globally. Join our founding organiser community.
          </p>
        </div>
      </section>

      {/* Value Props */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-14" data-testid="value-props-section">
        <div className="grid md:grid-cols-3 gap-6">
          <div className="bg-card rounded-xl p-8 shadow-custom text-center" data-testid="value-prop-free">
            <div className="flex justify-center mb-4">
              <Calendar size={40} style={{ color: '#3B2314' }} />
            </div>
            <h3 className="text-xl font-bold mb-2" style={{ color: '#3B2314' }}>Free to list forever</h3>
            <p className="text-muted-foreground">No subscription, no listing fees. Keep more of your ticket revenue.</p>
          </div>
          <div className="bg-card rounded-xl p-8 shadow-custom text-center" data-testid="value-prop-audience">
            <div className="flex justify-center mb-4">
              <Users size={40} style={{ color: '#3B2314' }} />
            </div>
            <h3 className="text-xl font-bold mb-2" style={{ color: '#3B2314' }}>Reach a targeted cultural audience</h3>
            <p className="text-muted-foreground">Connect directly with Afro-Caribbean and cultural communities across cities.</p>
          </div>
          <div className="bg-card rounded-xl p-8 shadow-custom text-center" data-testid="value-prop-verified">
            <div className="flex justify-center mb-4">
              <Shield size={40} style={{ color: '#3B2314' }} />
            </div>
            <h3 className="text-xl font-bold mb-2" style={{ color: '#3B2314' }}>Get verified and build your reputation</h3>
            <p className="text-muted-foreground">Earn a Verified Organiser badge and grow trust with every event.</p>
          </div>
        </div>
      </section>

      {/* Founding Organiser Benefits */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-10 py-10" data-testid="founding-benefits-section">
        <div className="bg-card rounded-xl p-8 shadow-custom">
          <div className="flex items-center gap-3 mb-6">
            <Star size={28} style={{ color: '#C08A2E' }} />
            <h2 className="text-3xl font-bold" style={{ color: '#3B2314' }}>Founding Organiser Benefits</h2>
          </div>
          <ul className="space-y-3">
            <li className="flex items-start gap-3" data-testid="benefit-pro">
              <Check size={22} style={{ color: '#3B2314' }} className="mt-1 shrink-0" />
              <span className="text-base">Free Pro account for the first year</span>
            </li>
            <li className="flex items-start gap-3" data-testid="benefit-verified">
              <Check size={22} style={{ color: '#3B2314' }} className="mt-1 shrink-0" />
              <span className="text-base">Verified badge from day one</span>
            </li>
            <li className="flex items-start gap-3" data-testid="benefit-featured">
              <Check size={22} style={{ color: '#3B2314' }} className="mt-1 shrink-0" />
              <span className="text-base">Featured placement on the homepage during launch period</span>
            </li>
            <li className="flex items-start gap-3" data-testid="benefit-support">
              <Check size={22} style={{ color: '#3B2314' }} className="mt-1 shrink-0" />
              <span className="text-base">Direct support from the Gatherly team</span>
            </li>
          </ul>
        </div>
      </section>

      {/* Application Form */}
      <section className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-10 py-10 pb-20" data-testid="application-form-section">
        <div className="bg-card rounded-xl p-8 shadow-custom">
          <h2 className="text-2xl font-bold mb-6" style={{ color: '#3B2314' }}>Apply to be a founding organiser</h2>

          {submitted ? (
            <div
              className="p-6 border rounded-lg text-center"
              style={{ backgroundColor: '#F5ECD7', borderColor: '#C4A57B' }}
              data-testid="application-confirmation"
            >
              <div className="flex justify-center mb-3">
                <Check size={36} style={{ color: '#3B2314' }} />
              </div>
              <p className="text-lg font-medium" style={{ color: '#3B2314' }}>
                Thank you for applying. We will be in touch within 48 hours.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="application-form">
              <div>
                <label className="block text-sm font-medium mb-2">
                  Full name <span className="text-destructive">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={formData.full_name}
                  onChange={handleChange('full_name')}
                  className="w-full px-4 py-2 border border-border rounded-lg"
                  data-testid="application-full-name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Email address <span className="text-destructive">*</span>
                </label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleChange('email')}
                  className="w-full px-4 py-2 border border-border rounded-lg"
                  data-testid="application-email"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Instagram or website link <span className="text-destructive">*</span>
                </label>
                <input
                  type="url"
                  required
                  placeholder="https://"
                  value={formData.social_link}
                  onChange={handleChange('social_link')}
                  className="w-full px-4 py-2 border border-border rounded-lg"
                  data-testid="application-social-link"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Types of events you run <span className="text-destructive">*</span>
                </label>
                <textarea
                  required
                  rows="4"
                  value={formData.event_types}
                  onChange={handleChange('event_types')}
                  className="w-full px-4 py-2 border border-border rounded-lg resize-none"
                  data-testid="application-event-types"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  City you are based in <span className="text-destructive">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={formData.city}
                  onChange={handleChange('city')}
                  className="w-full px-4 py-2 border border-border rounded-lg"
                  data-testid="application-city"
                />
              </div>

              {errorMsg && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800" data-testid="application-error">
                  {errorMsg}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="w-full py-3 bg-primary text-primary-foreground rounded-lg font-medium hover:opacity-90 transition disabled:opacity-60"
                data-testid="application-submit"
              >
                {submitting ? 'Submitting…' : 'Apply to be a founding organiser'}
              </button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
